import type { WallShape } from '@walls/store';
import { getTextAttributes } from '../../drawing/utils/shapeAttributes';

export interface MeasuredTextShape {
  font: string;
  height: number;
  lineHeight: number;
  lines: string[];
  width: number;
  x: number;
  y: number;
}

const fallbackFontScale = 0.6;
const fallbackLineHeight = 1.2;

export function measureTextShape(
  shape: WallShape,
  context?: CanvasRenderingContext2D | null
): MeasuredTextShape {
  const attributes = getTextAttributes(shape);
  const lines = attributes.text.split('\n');
  const font = `${attributes.fontSize}px ${attributes.fontFamily}`;
  let width = 0;
  let measuredLineHeight = attributes.fontSize * fallbackLineHeight;

  if (context) {
    context.font = font;
    const heightMetrics = context.measureText(attributes.text || ' ');
    const metricHeight =
      (heightMetrics.fontBoundingBoxAscent ?? 0) + (heightMetrics.fontBoundingBoxDescent ?? 0);

    measuredLineHeight = metricHeight > 0 ? metricHeight : measuredLineHeight;
    width = lines.reduce((maxWidth, line) => Math.max(maxWidth, context.measureText(line).width), 0);
  } else {
    width = Math.max(...lines.map((line) => line.length), 1) * attributes.fontSize * fallbackFontScale;
  }

  return {
    font,
    height: lines.length * measuredLineHeight,
    lineHeight: measuredLineHeight,
    lines,
    width,
    x: attributes.x,
    y: attributes.y,
  };
}

export function getTextSelectionBorder(measured: MeasuredTextShape, zoom: number) {
  const lineThickness = Math.max(1, Math.floor(2 / zoom));

  return {
    height: measured.height + lineThickness * 2 + 4,
    lineThickness,
    width: measured.width + lineThickness * 2 + 8,
    x: measured.x - lineThickness - 4,
    y: measured.y - lineThickness - 4,
  };
}
